import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getPolicyDrafts, createPolicyDraft } from "@/lib/api";
import PolicyDraftDetail from "./PolicyDraftDetail";
import { PromptDialog } from "./PolicyDialogs";
import { StateBadge, fmtDate, who, Empty, errMsg } from "./policyShared";

function DraftCard({ d, active, onClick }) {
  return (
    <button onClick={onClick} className={`w-full text-left rounded-md border p-3 space-y-1 transition-colors ${active ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid={`policy-draft-card-${d.id}`}>
      <div className="flex items-center justify-between gap-2"><StateBadge state={d.state} />{d.emergency && <span className="text-[10px] text-rose-600">emergency</span>}</div>
      <div className="text-sm font-medium text-slate-900 truncate">{who(d, "created_by")}</div>
      <div className="text-[11px] text-slate-500">from {d.base_version_label} · {fmtDate(d.updated_at || d.created_at)}{d.created_channel && d.created_channel !== "portal" ? ` · via ${d.created_channel}` : ""}</div>
      {d.notes && <div className="text-[11px] text-slate-600 line-clamp-2">{d.notes}</div>}
      <div className="text-[11px] text-slate-400">{d.test_report ? (d.test_report_stale ? "test report stale" : "tests run") : "tests not run"}</div>
    </button>
  );
}

export default function PolicyDrafts({ schema, groups, canEdit, canApprove, onChanged, initialDraftId }) {
  const [drafts, setDrafts] = useState(null);
  const [sel, setSel] = useState(initialDraftId || null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    const r = await getPolicyDrafts();
    setDrafts(r.drafts);
    setSel((s) => (s && r.drafts.some((d) => d.id === s) ? s : r.drafts[0]?.id || null));
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  const changed = async () => { await load(); onChanged?.(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="policy-drafts">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Open drafts ({drafts?.length ?? "…"})</div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => load()} title="Refresh" data-testid="policy-drafts-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
            {canEdit && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCreating(true)} data-testid="policy-new-draft-btn"><Plus className="w-4 h-4 mr-1" /> New draft</Button>}
          </div>
        </div>
        {drafts && !drafts.length && <Empty testId="policy-drafts-empty">No open drafts. {canEdit ? "Start one from Live, or let Claude draft via the Policy Centre MCP." : ""}</Empty>}
        {(drafts || []).map((d) => <DraftCard key={d.id} d={d} active={d.id === sel} onClick={() => setSel(d.id)} />)}
        <div className="text-[11px] text-slate-400 pt-1">Workflow: Draft → test set → Pending approval → Approved (second person) → Published. One open draft per author.</div>
      </div>
      <div className="min-w-0">
        {sel ? <PolicyDraftDetail key={sel} draftId={sel} schema={schema} groups={groups} canEdit={canEdit} canApprove={canApprove} onChanged={changed} />
          : drafts && <Empty testId="policy-draft-none-selected">Select a draft to review its changes against Live.</Empty>}
      </div>
      <PromptDialog open={creating} onClose={() => setCreating(false)} title="New draft from Live" description="Copies the Live document into a draft under your name. Propose changes setting by setting, run the test set, then submit for a second person to approve." label="What is this draft for?" confirmLabel="Create draft" testId="policy-new-draft"
        onConfirm={async (notes) => { const d = await createPolicyDraft({ notes }); toast.success("Draft created"); await load(); setSel(d.id); onChanged?.(); }} />
    </div>
  );
}
